import React, { ChangeEvent, forwardRef, useEffect, useState } from "react";
import "./ReactHookInput.scss";
import * as _ from "lodash-es";
import ISupplierModel from "../../../models/ISupplierModel";
import SupplierService from "../../../services/SupplierService";
import UIService from "../../../services/UIService";
import InputMask from "react-input-mask";
import { useIntl, IntlShape } from "react-intl";
import { FieldError, UseFormRegisterReturn, FieldPath, FieldValues } from "react-hook-form";

export interface IReactHookInputProps {
    // React Hook Form integration
    register?: UseFormRegisterReturn;
    error?: FieldError;

    // Basic input props
    id?: string;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    name: string;
    type?: "number" | "email" | "password" | "text" | "tel" | "radio" | "checkbox";
    autoComplete?: boolean | string;
    autoFocus?: boolean;

    // Validation props
    isRequired?: boolean;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number | "any";
    showStep?: boolean;

    // Styling props
    isSmall?: boolean;
    defaultClassName?: string;
    appendText?: string;
    prependText?: string;

    // Supplier-specific props
    supplierModel?: ISupplierModel;
    isSupplierVendorOverride?: boolean;
    disablePrecisionCheck?: boolean;

    // Behavior props
    tabIndex?: number;
    disableKeyUp?: boolean;
    editMode?: boolean;
    returnZero?: boolean;
    title?: string;
    testId?: string;

    // Masked input props
    maskedInput?: boolean;
    mask?: string;
    maskChar?: string;
    alwaysShowMask?: boolean;

    // Event handlers (optional for react-hook-form compatibility)
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
    onWheel?: (e: React.WheelEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

    // Legacy support for non-react-hook-form usage
    value?: any;
    fn?: (e: ChangeEvent<HTMLInputElement>) => void;
    checked?: boolean; // For checkboxes
}

export const ReactHookInput = forwardRef<HTMLInputElement, IReactHookInputProps>((props, ref) => {
    const intl = useIntl();
    const [indicatorBarPercentage, setIndicatorBarPercentage] = useState(100);

    // Handle legacy onChange function
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // For react-hook-form, we need to be more careful about value manipulation
        if (props.register) {
            // Let react-hook-form handle the change first
            if (props.register.onChange) {
                props.register.onChange(e);
            }

            // Then apply our custom logic without modifying the event
            if (props.maxLength && value.toString().length > props.maxLength) {
                // Don't return early, let react-hook-form handle it
            }

            // Call custom onChange
            if (props.onChange) {
                props.onChange(e);
            }

            // Call legacy fn prop
            if (props.fn) {
                props.fn(e);
            }

            return;
        }

        // Legacy behavior for non-react-hook-form usage
        // Handle maxLength
        if (props.maxLength && value.toString().length > props.maxLength) {
            return;
        }

        // Handle returnZero
        if (_.isNil(value) && props.returnZero) {
            e.target.value = "0";
        }

        // Handle number type
        if (props.type === "number" && value && Number(value) > 0) {
            e.target.value = Number(e.target.value).toString();
        }

        // Call custom onChange
        if (props.onChange) {
            props.onChange(e);
        }

        // Call legacy fn prop
        if (props.fn) {
            props.fn(e);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        UIService.iOSKeyboardOpenFix();

        // Call register onBlur first (for react-hook-form)
        if (props.register?.onBlur) {
            props.register.onBlur(e);
        }

        // Call custom onBlur
        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.disableKeyUp) {
            return;
        }
        // Convert KeyboardEvent to ChangeEvent for legacy compatibility
        const changeEvent = e as any;
        handleChange(changeEvent);
    };

    // Process placeholder with intl and supplier overrides
    const getProcessedPlaceholder = () => {
        let placeholder = props.placeholder || "";
        let dashedPlaceholder = placeholder.replace(/ /g, "_");

        if (props.isSupplierVendorOverride && props.supplierModel?.vendor_name_override) {
            placeholder = placeholder.replace(/vendor/g, props.supplierModel.vendor_name_override);
            dashedPlaceholder = dashedPlaceholder.replace(/vendor/g, props.supplierModel.vendor_name_override);
        }

        if (placeholder.length) {
            try {
                const messageProps: any = {
                    id: dashedPlaceholder,
                    propName: "",
                };
                return intl.formatMessage(messageProps);
            } catch {
                return placeholder;
            }
        }

        return placeholder;
    };

    // Process value with supplier-specific logic
    const getProcessedValue = () => {
        // For react-hook-form, let the register handle the value entirely
        if (props.register) {
            return undefined; // Don't override react-hook-form's value management
        }

        let value = props.value;

        if (!_.isNil(value)) {
            // Handle qty precision for supplier
            if (props.name.indexOf("qty") >= 0 && !props.disablePrecisionCheck) {
                const supplier: ISupplierModel = props.supplierModel || SupplierService.getLocalSupplierDataFn();

                if (supplier?.qty_precision !== undefined && supplier.qty_precision === 0) {
                    value = parseFloat(value).toFixed(supplier.qty_precision);
                }
            }
            return value;
        }

        return "";
    };

    // Build className
    const getClassName = () => {
        let className = props.className || "";

        if (props.isSmall) {
            className += " form-control-sm";
        }

        // Handle error state
        let errorState = false;
        if (props.error) {
            errorState = true;
        } else if (props.isRequired && !props.register) {
            // Only do manual required check for non-RHF usage;
            // RHF manages its own validation via the error prop
            const currentValue = getProcessedValue();
            if (props.minLength) {
                if (currentValue && currentValue.length < props.minLength) {
                    errorState = true;
                }
            } else {
                if (_.isNil(currentValue) || !currentValue.length) {
                    errorState = true;
                }
            }
        }

        let defaultClassName = " form-control form-control-sm ";
        if (props.type === "radio" || props.type === "checkbox") {
            defaultClassName = " ";
        }
        if (props.defaultClassName) {
            defaultClassName = ` ${props.defaultClassName} `;
        }

        return className + defaultClassName + (errorState ? "is-invalid alert-danger" : "");
    };

    // Handle autoComplete
    const getAutoComplete = (): string | undefined => {
        if (props.autoComplete === false) return "off";
        if (props.autoComplete === true) return "on";
        if (typeof props.autoComplete === "string") return props.autoComplete;
        return "on";
    };

    // Handle step and min/max for numbers
    const getNumberProps = () => {
        let step: any = props.step;
        let min = props.min;
        const max = props.max;

        if (props.showStep) {
            step = props.step || "any";
            min = !_.isNil(props.min) ? props.min : 1;
        }

        return { step, min, max };
    };

    const { step, min, max } = getNumberProps();
    const processedValue = getProcessedValue();
    const processedPlaceholder = getProcessedPlaceholder();
    const compiledClassName = getClassName();

    // Additional props for number inputs
    const additionalProps: any = {};
    if (props.type === "number") {
        additionalProps.inputMode = "numeric";
        additionalProps.pattern = "d*";
        additionalProps["disable-autocomplete"] = "true";
    }

    // Combine register props with our props, letting react-hook-form take precedence
    const inputProps = {
        "data-testid": props.testId || props.name,
        id: props.id,
        autoFocus: props.autoFocus,
        title: props.title,
        type: props.type || "text",
        className: compiledClassName,
        "aria-label": props.name,
        placeholder: processedPlaceholder,
        onClick: props.onClick,
        onKeyUp: handleKeyUp,
        onKeyDown: props.onKeyDown,
        disabled: props.disabled,
        checked: props.checked,
        autoComplete: getAutoComplete(),
        required: props.isRequired,
        minLength: props.minLength,
        maxLength: props.maxLength,
        min,
        max,
        step,
        tabIndex: props.tabIndex,
        onWheel: props.onWheel,
        "data-lpignore": "true",
        ref,
        ...additionalProps,
        // For react-hook-form, spread register props and let them override
        ...(props.register || {}),
        // For non-react-hook-form usage, set value and onChange
        ...(props.register
            ? {}
            : {
                  value: processedValue,
                  onChange: handleChange,
                  name: props.name,
              }),
        // If using react-hook-form but need custom onChange behavior
        ...(props.register && (props.onChange || props.fn)
            ? {
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      // Call react-hook-form's onChange first
                      if (props.register?.onChange) {
                          props.register.onChange(e);
                      }
                      // Then call custom handlers
                      if (props.onChange) {
                          props.onChange(e);
                      }
                      if (props.fn) {
                          props.fn(e);
                      }
                  },
              }
            : {}),
        // Handle onBlur for both react-hook-form and custom usage
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            // Call react-hook-form's onBlur first
            if (props.register?.onBlur) {
                props.register.onBlur(e);
            }
            // Then call our custom onBlur
            handleBlur(e);
        },
    };

    // Create input element
    let inputElement = <input {...inputProps} />;

    // Handle masked input
    if (props.maskedInput && props.mask) {
        inputElement = <InputMask mask={props.mask} maskChar={props.maskChar} alwaysShowMask={props.alwaysShowMask} {...inputProps} />;
    }

    // Handle edit mode
    if (props.editMode === false) {
        return <span>{processedValue || ""}</span>;
    }

    // Handle input groups with prepend/append text
    if (props.appendText && props.prependText) {
        return (
            <div className="input-group input-group-sm">
                <div className="input-group-prepend">
                    <span className="input-group-text">{props.prependText}</span>
                </div>
                {inputElement}
                <div className="input-group-append">
                    <span className="input-group-text">{props.appendText}</span>
                </div>
            </div>
        );
    } else if (props.appendText) {
        return (
            <div className="input-group input-group-sm">
                {inputElement}
                <span className="input-group-text">{props.appendText}</span>
            </div>
        );
    } else if (props.prependText) {
        return (
            <div className="input-group input-group-sm">
                <div className="input-group-prepend">
                    <span className="input-group-text">{props.prependText}</span>
                </div>
                {inputElement}
            </div>
        );
    }

    return inputElement;
});

ReactHookInput.displayName = "ReactHookInput";
